import { Component, Input, OnInit } from '@angular/core';
import { Params, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * A reusable back button component that handles navigation to previous pages.
 *
 * This component provides context-aware navigation:
 * - On 'search' or 'vaccine-history' pages: Uses stored path and query parameters from sessionStorage
 * - On all other pages: Navigates to a configurable fallback route
 *
 * The component reads navigation data from sessionStorage keys:
 * - 'backLinkPath': The path to navigate back to
 * - 'backLinkQueryParams': Query parameters stored as a JSON string
 *
 * @example
 * // Basic usage with default values
 * <app-back-button></app-back-button>
 *
 * @example
 * // Custom button text and fallback route
 * <app-back-button
 *   backLabel="Return to Dashboard"
 *   fallbackRoute="/dashboard">
 * </app-back-button>
 *
 * @example
 * // Store navigation data before navigating to a patient page
 * storeBackNavigation() {
 *   sessionStorage.setItem('backLinkPath', '/search');
 *   sessionStorage.setItem('backLinkQueryParams', JSON.stringify({ query: 'vaccines' }));
 *   this.router.navigate(['/search/patient/123']);
 * }
 *
 * @Input **backLabel** - The text to display on the back button (default: `'Go back'`)
 * @Input **fallbackRoute** - The route to navigate to if no back link path is available or not on a patient page (default: `'/home'`)
 * @Input **class** - Add class to the component.
 */

@Component({
  selector: 'app-back-button',
  imports: [],
  templateUrl: './back-button.component.html',
  styleUrl: './back-button.component.css',
  standalone: true,
})
export class BackButtonComponent implements OnInit {
  @Input() backLabel = 'Go back';
  @Input() fallbackRoute = '/home';

  backLinkPath: string | null = null;
  backLinkQueryParams: Params | null = null;
  currentUrl = '';

  constructor(private _router: Router) {
    this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.currentUrl = event.url;
    });
  }

  ngOnInit(): void {
    this.backLinkPath = sessionStorage.getItem('backLinkPath');
    const queryParamsString = sessionStorage.getItem('backLinkQueryParams');
    this.backLinkQueryParams = queryParamsString ? JSON.parse(queryParamsString) : null;
  }

  goBack(): void {
    const isPatientPage = this.isPatientPage();
    if (isPatientPage && this.backLinkPath) {
      if (this.backLinkQueryParams) {
        this._router
          .navigate([this.backLinkPath], {
            queryParams: this.backLinkQueryParams,
          })
          .catch((error) => console.error('Navigation error:', error));
      } else {
        this._router.navigate([this.backLinkPath]).catch((error) => console.error('Navigation error:', error));
      }
    } else {
      this._router.navigate([this.fallbackRoute]).catch((error) => console.error('Navigation error:', error));
    }
  }

  private isPatientPage(): boolean {
    const url = this.currentUrl.split('?')[0];
    return url.includes('/search/patient/') || url.includes('/vaccine-history/patient/');
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.goBack();
    }
  }
}
